import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useApp from "../../hooks/useApp";
import { Request_Error } from "../../assets/images";
import {
  CheckBox,
  PhoneNumberInput,
  CustomButton,
  CustomMessageModal,
} from "../../components";
import {
  ROUTES,
  NEXT_AUTH_FACTOR,
  ACCOUNT_STATE,
  EVENT_NAME,
} from "../../constants";
import api from "../../services";
import useNavigateWithQuery from "../../hooks/useNavigateWithQuery";
import { convertTimestamp } from "../../utils";
import { logAnalyticEvent } from "../../analytics";
import "./styles.scss";

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithQuery();
  const {
    changeLanguage,
    selectedLanguage,
    isRTL,
    tenantConfig,
    isTCAccepted,
    handleIsTCAccepted,
    phoneNumber,
    setPhoneNumber,
  } = useApp();

  const [startTime, setStartTime] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [messageModalData, setMessageModalData] = useState(null);

  const isValidNumber = () => {
    const countryCode = selectedCountry?.country_code || "";
    const number = `${countryCode}${phoneNumber || ""}`;
    if (selectedCountry?.custom_regex) {
      return !!number.match(selectedCountry.custom_regex);
    }
    return false;
  };
  const handleNavigation = (nextAuthFactor, params) => {
    const endTime = Date.now();
    setPhoneNumber("");
    handleIsTCAccepted(!isTCAccepted);
    logAnalyticEvent(EVENT_NAME.PAGE_VIEW, {
      page_title: ROUTES.REGISTER,
      path: `${window.location.origin}/${ROUTES.REGISTER}`,
      time_spent: convertTimestamp(endTime - startTime),
    });
    switch (nextAuthFactor) {
      case NEXT_AUTH_FACTOR.VERIFY_OTP:
        navigate(`/${ROUTES.VERIFY_OTP}`, { state: params });
        break;
      case NEXT_AUTH_FACTOR.VERIFY_PIN:
        navigate(`/${ROUTES.VERIFY_PIN}`, {
          state: {
            ...params,
            isNotFirstTime: true,
            accountState: ACCOUNT_STATE.LOGIN,
          },
        });
        break;
      case NEXT_AUTH_FACTOR.SET_PIN:
        navigate(`/${ROUTES.VERIFY_PIN}`, { state: params });
        break;
      default:
        break;
    }
  };

  const checkIsCutomerExists = async () => {
    try {
      setIsLoading(true);
      const countryCode = selectedCountry?.country_code;
      const number = `${countryCode}${phoneNumber}`;

      const payload = { username: number };

      const response = await api.customerExists(payload);

      if (response?.data?.exists) {
        const loginInitiateRes = await api.loginInitiate(payload);

        handleNavigation(loginInitiateRes?.next_auth_factor, {
          phoneNumber: number,
          isNewCustomer: false,
        });
      } else {
        const registerInitiateRes = await api.registerInitiate(payload);

        handleNavigation(registerInitiateRes?.next_auth_factor, {
          phoneNumber: number,
          isNewCustomer: true,
        });
      }
    } catch (error) {
      setMessageModalData({
        heading: "Oops! Something Went Wrong",
        description: `${error?.response?.data?.message || error?.message}`,
        msgImage: Request_Error,
      });
      logAnalyticEvent(EVENT_NAME.ERROR_EVENT, {
        page_title: ROUTES.REGISTER,
        error: `${error?.response?.data?.message || error?.message}`,
      });
      console.error("Error in checkIsCutomerExists method: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnPressNext = () => {
    const isValid = isValidNumber();
    if (isValid) {
      checkIsCutomerExists();
    } else {
      setErrorMessage(selectedCountry?.message[selectedLanguage?.value]);
    }
  };

  const handleOnChange = (event) => {
    const { value } = event.target;
    setPhoneNumber(value);
    setIsButtonEnabled(value.length > 0);
    setErrorMessage("");
  };

  const handleOnBlur = () => {
    const isValid = isValidNumber();
    if (!isValid) {
      setErrorMessage(selectedCountry?.message[selectedLanguage?.value]);
    }
  };

  useEffect(() => {
    if (tenantConfig?.phone_number_policy.content.allowed_countries.length) {
      const allowedCountries =
        tenantConfig?.phone_number_policy?.content.allowed_countries;
      setSelectedCountry(allowedCountries[0]);
    }
  }, [tenantConfig?.phone_number_policy]);

  useEffect(() => {
    setIsButtonEnabled(phoneNumber.length > 0 && isTCAccepted);
  }, [phoneNumber, isTCAccepted]);

  useEffect(() => {
    setStartTime(Date.now());
  }, []);

  return (
    <div className="register-container">
      <div className="content-container">
        <span
          className={`language-btn ${isRTL ? "language-btn-rtl" : ""}`}
          onClick={() =>
            changeLanguage(selectedLanguage?.value === "ar" ? "en" : "ar")
          }
        >
          {selectedLanguage?.value === "ar" ? "English" : "العربية"}
        </span>
        <img
          src={`${tenantConfig?.favicon.content.dark}`}
          alt="logo"
          className="logo"
        />
        <h1 className="title">{t("phoneNo.phone_no_heading")}</h1>
        <p className="description">{t("phoneNo.phone_no_subheading")}</p>
        <PhoneNumberInput
          isoCode={selectedCountry?.iso_code}
          countryCode={selectedCountry?.country_code}
          value={phoneNumber}
          onChange={handleOnChange}
          maxLength={selectedCountry?.phone_number_length}
          error={errorMessage}
          onBlur={handleOnBlur}
          ContainerStyle={{ marginLeft: 0, marginRight: 0 }}
        />
      </div>
      <div className="footer-container">
        <CheckBox
          value={isTCAccepted}
          onChange={() => handleIsTCAccepted(!isTCAccepted)}
        >
          <span className="footer-text">
            {t("phoneNo.phone_no_footerText1", { tenant: tenantConfig?.name })}{" "}
            <span
              className="footer-link"
              onClick={() =>
                navigate(`/${ROUTES.TERMS_AND_CONDITIONS}`, {
                  state: { showButton: true },
                })
              }
            >
              {t("phoneNo.phone_no_footerText2")}
            </span>
          </span>
        </CheckBox>
        <CustomButton
          disabled={!isButtonEnabled || isLoading}
          isLoading={isLoading}
          text={t("global.next")}
          onClick={handleOnPressNext}
        />
      </div>
      <CustomMessageModal
        show={!!messageModalData}
        onClickClose={() => setMessageModalData(null)}
        image={messageModalData?.msgImage}
        heading={messageModalData?.heading}
        description={messageModalData?.description}
      />
    </div>
  );
};

export default Register;
